import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Filter, ArrowUpDown, Eye, ShoppingCart, Loader2, Info, Wallet, Zap, ShieldCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { marketApi, purchaseApi, authApi, balanceApi } from '@/lib/api';
import { TradeUrlRequiredDialog } from '@/components/TradeUrlRequiredDialog';
import { OrderStatusModal } from '@/components/OrderStatusModal';
import { ItemAttributes, FloatBar, StickerRow } from '@/components/ItemAttributes';
import type { StickerLike } from '@/components/ItemAttributes';
import { reachGoal, GOAL_PURCHASE_CLICK, GOAL_PURCHASE_ERROR, GOAL_PURCHASE_CONFIRM } from '@/lib/metrika';

interface ProductCard {
  id: number;
  slug: string;
  market_hash_name: string;
  title: string;
  description: string;
  weapon_name: string;
  skin_name: string;
  image: string;
  rarity: string;
  rarity_color: string;
  collection: string;
  category?: string;
}

interface Listing {
  id: number;
  full_name: string;
  price: number;
  currency: string;
  wear: string;
  float_value: number | null;
  paint_seed: number | null;
  is_stattrak: boolean;
  is_souvenir: boolean;
  has_stickers: boolean;
  stickers: StickerLike[];
  // Из расширенного прайса Xpanda. Пустые у позиций без экземпляров
  // (кейсы, наклейки) и до раската расширенного прайса.
  paint_index?: number | null;
  phase?: string | null;
  keychains?: StickerLike[];
  nametag?: string | null;
  inspect_link?: string | null;
  asset_id?: string | null;
  offer_id?: string | null;
  /** Ручная выдача (delivery=1) — до 12 часов вместо ~минуты. */
  slow_delivery?: boolean;
}

interface RelatedVersion {
  slug: string;
  title: string;
  lowest_price: number | null;
}

interface ProductData {
  canonical_url: string;
  meta: {
    title: string;
    description: string;
    og_image: string;
  };
  card: ProductCard;
  aggregate_offer: {
    low_price: number;
    high_price: number;
    offer_count: number;
    price_currency: string;
  };
  listings: Listing[];
  available_filters: {
    wears: { value: string; label: string; count: number }[];
    has_stattrak: boolean;
    stattrak_count: number;
    has_stickers: boolean;
    stickers_count: number;
    float_range: { min: number; max: number };
  };
  related_versions: {
    normal: RelatedVersion | null;
    stattrak: RelatedVersion | null;
    // Сувенирная версия бывает заметно дешевле обычной — без неё в
    // селекторе пользователь этот вариант не видел.
    souvenir?: RelatedVersion | null;
  };
}



/** Детерминированный счётчик просмотров (60% → 10–50; 40% → 51–170). */
function getFakeViewCount(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  h = Math.abs(h);
  return (h % 10) < 6 ? 10 + (h % 41) : 51 + (h % 120);
}

const WEAR_RU: Record<string, string> = {
  'Factory New':    'Прямо с завода',
  'Minimal Wear':   'Немного поношенное',
  'Field-Tested':   'После полевых испытаний',
  'Well-Worn':      'Поношенное',
  'Battle-Scarred': 'Закалённое в боях',
};
const wearRu = (w?: string) => (w && WEAR_RU[w]) || w || '—';

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [product, setProduct] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Фильтры
  const [selectedWear, setSelectedWear] = useState<string | null>(searchParams.get('wear'));
  const [stattrakOnly, setStattrakOnly] = useState(searchParams.get('stattrak') === '1');
  const [stickersOnly, setStickersOnly] = useState(searchParams.get('stickers') === '1');

  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'price_asc');

  // Покупка
  const [showTradeUrlDialog, setShowTradeUrlDialog] = useState(false);
  const [buying, setBuying] = useState(false);
  const [cheapestListing, setCheapestListing] = useState<Listing | null>(null);
  const [orderStatusTxId, setOrderStatusTxId] = useState<number | null>(null);
  const [pendingPayment, setPendingPayment] = useState<{url: string; formData: Record<string, string>} | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  // Ссылка на popup-окно оплаты — для закрытия при подтверждении оплаты
  const paymentPopupRef = useRef<Window | null>(null);

  useEffect(() => {
    if (authApi.isAuthenticated()) {
      balanceApi.getBalance()
        .then((d) => setBalance((d as { balance: number }).balance))
        .catch(() => setBalance(null));
    }
    // Восстанавливаем txId если мобильный браузер ушёл на страницу оплаты и вернулся
    const savedTx = sessionStorage.getItem('xpanda_pending_tx');
    if (savedTx) {
      setOrderStatusTxId(Number(savedTx));
      sessionStorage.removeItem('xpanda_pending_tx');
    }
  }, []);


  useEffect(() => {
    loadProduct();
  }, [slug, selectedWear, stattrakOnly, stickersOnly, sortBy]);


  // Обновляем meta теги для SEO
  useEffect(() => {
    if (product?.meta) {
      document.title = product.meta.title;
      
      // Meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', product.meta.description);
      
      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `https://market.xpanda.pro${product.canonical_url}`);
      
      // Open Graph
      updateMetaTag('og:title', product.meta.title);
      updateMetaTag('og:description', product.meta.description);
      updateMetaTag('og:image', product.meta.og_image);
      updateMetaTag('og:type', 'product');
      updateMetaTag('og:url', `https://market.xpanda.pro${product.canonical_url}`);
      
      // Schema.org AggregateOffer
      const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.card.title,
        image: product.card.image,
        description: product.card.description,
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: product.aggregate_offer.low_price,
          highPrice: product.aggregate_offer.high_price,
          offerCount: product.aggregate_offer.offer_count,
          priceCurrency: product.aggregate_offer.price_currency,
          availability: 'https://schema.org/InStock'
        }
      };
      
      let schemaScript = document.getElementById('schema-product') as HTMLScriptElement | null;
      if (!schemaScript) {
        schemaScript = document.createElement('script') as HTMLScriptElement;
        schemaScript.id = 'schema-product';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schemaData);
    }
  }, [product]);

  const updateMetaTag = (property: string, content: string) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedWear) params.set('wear', selectedWear);
      if (stattrakOnly) params.set('stattrak', '1');
      if (stickersOnly) params.set('stickers', '1');
      params.set('sort', sortBy);
      
      const data = await marketApi.getProductCard(slug!, params.toString());
      setProduct(data);
      
      // Находим самый дешевый листинг
      if (data.listings && data.listings.length > 0) {
        const cheapest = data.listings.reduce((min: Listing, item: Listing) => 
          item.price < min.price ? item : min
        );
        setCheapestListing(cheapest);
      } else {
        setCheapestListing(null);
      }
    } catch (error: any) {
      toast.error('Ошибка загрузки товара: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyClick = () => {
    if (buying) return;  // защита от двойного клика
    if (!authApi.isAuthenticated()) {
      toast.error('Необходимо войти через Steam для покупки');
      navigate('/login');
      return;
    }
    if (!cheapestListing) {
      toast.error('Нет доступных предложений');
      return;
    }
    reachGoal(GOAL_PURCHASE_CLICK, { listing_id: cheapestListing?.id, product: slug });
    handleConfirmBuy();
  };

  const handleConfirmBuy = async (paymentMethod: 'invizo' | 'balance' = 'invizo') => {
    if (!cheapestListing) return;

    setBuying(true);
    try {
      const result = await purchaseApi.createPurchase(cheapestListing.id, paymentMethod, '', '', false);

      // Balance flow: backend синхронно reserve→Xpanda→confirm, возвращает
      // status='completed' (или 502 при Xpanda fail → ловится в catch).
      if (paymentMethod === 'balance') {
        if (result.status === 'completed') {
          reachGoal(GOAL_PURCHASE_CONFIRM, { product: slug, method: 'balance' });
          toast.success('Куплено за баланс! Скин будет отправлен.');
          if (result.transaction_id) setOrderStatusTxId(result.transaction_id);
          balanceApi.getBalance()
            .then((d) => setBalance((d as { balance: number }).balance))
            .catch(() => {});
        } else {
          toast.error('Не удалось купить за баланс');
        }
        return;
      }

      // PC возвращает status='pending' для invizo-purchase (PurchaseResponsePending
      // schema). Принимаем оба варианта (pending_payment — legacy от pre-cutout кода).
      if (result.status === 'pending' || result.status === 'pending_payment') {
        if (result.form_data && result.payment_url) {
          // Открываем модал статуса сразу
          if (result.transaction_id) {
            setOrderStatusTxId(result.transaction_id);
            // На мобильных браузер может уйти на страницу оплаты — сохраняем txId
            sessionStorage.setItem('xpanda_pending_tx', String(result.transaction_id));
          }
          // Сохраняем данные для повторного открытия из OrderStatusModal
          setPendingPayment({ url: result.payment_url, formData: result.form_data });
          // Сохраняем в localStorage — кнопка «Открыть страницу оплаты» работает из истории профиля
          if (result.transaction_id) {
            try { localStorage.setItem(`xpanda_payment_${result.transaction_id}`, JSON.stringify({ url: result.payment_url, formData: result.form_data })); } catch {}
          }

          const isMobile = /Mobi|Android/i.test(navigator.userAgent);
          if (isMobile) {
            // На мобильных не открываем popup — пользователь нажмёт «Открыть страницу оплаты» в модале
            toast.success('Нажмите «Оплатить» в окне статуса для перехода к оплате');
          } else {
            toast.success('Перенаправляем на оплату...');
            try {
              // Открываем popup ДО submit — синхронно в click-handler, браузер не блокирует.
              // Если popup заблокирован (null) — fallback к новой вкладке.
              const popup = window.open(
                '',
                'xpanda-payment',
                'width=560,height=700,scrollbars=yes,resizable=yes',
              );
              paymentPopupRef.current = popup; // сохраняем для автозакрытия

              const form = document.createElement('form');
              form.method = 'POST';
              form.action = result.payment_url;
              form.target = popup ? 'xpanda-payment' : '_blank';
              form.enctype = 'application/x-www-form-urlencoded';
              form.style.display = 'none';

              Object.entries(result.form_data).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = String(value);
                form.appendChild(input);
              });

              document.body.appendChild(form);
              form.submit();
              document.body.removeChild(form);

              if (!popup) {
                toast('Оплата открыта в новой вкладке — разрешите всплывающие окна для лучшего опыта', { duration: 5000 });
              }
            } catch (err) {
              console.error('[PAYMENT] Error creating/submitting form:', err);
              toast.error('Ошибка при создании формы оплаты');
            }
          }

          return;
        }

        reachGoal(GOAL_PURCHASE_ERROR, { product: slug, reason: 'no_form_data' });
        toast.error('Ошибка создания платежа: нет данных формы');
      } else {
        reachGoal(GOAL_PURCHASE_ERROR, { product: slug, reason: 'bad_status' });
        toast.error('Ошибка создания платежа');
      }
    } catch (error: any) {
      if (error.message?.includes('Steam trade URL') || error.message?.includes('trade_url')) {
        setShowTradeUrlDialog(true);
      } else {
        reachGoal(GOAL_PURCHASE_ERROR, { product: slug, reason: error.message });
        toast.error('Ошибка покупки: ' + error.message);
      }
    } finally {
      setBuying(false);
    }
  };

  const updateFilters = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
      else newParams.delete(key);
    });
    setSearchParams(newParams);
  };

  const handleWearToggle = (wear: string) => {
    const newWear = selectedWear === wear ? null : wear;
    setSelectedWear(newWear);
    updateFilters({ wear: newWear });
  };

  const handleStattrakToggle = (checked: boolean) => {
    setStattrakOnly(checked);
    updateFilters({ stattrak: checked ? '1' : null });
  };

  const handleStickersToggle = (checked: boolean) => {
    setStickersOnly(checked);
    updateFilters({ stickers: checked ? '1' : null });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Товар не найден</h1>
          <Link to="/" className="text-[#ff6b00] hover:underline">Вернуться на главную</Link>
        </div>
      </div>
    );
  }

  const { card, aggregate_offer, listings, available_filters } = product;

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* Breadcrumbs */}
      <div className="bg-[#161b22] border-b border-[#30363d]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3 max-w-[1920px] mx-auto">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-[#8b949e] hover:text-white">Главная</Link>
            <ChevronRight className="w-4 h-4 text-[#8b949e]" />
            <Link to={`/?search=${encodeURIComponent(card.weapon_name)}`} className="text-[#8b949e] hover:text-white">{card.weapon_name}</Link>
            <ChevronRight className="w-4 h-4 text-[#8b949e]" />
            <span className="text-white">{card.skin_name}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-[1920px] mx-auto">
        {/* Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Image */}
          <div className="relative bg-[#161b22] rounded-2xl p-8 border border-[#30363d] flex items-center justify-center">
            {/* Оверлей: время доставки — сверху-слева.
                Был жёстко зашит на «1 мин» с зелёной молнией, поэтому у
                двенадцатичасовых товаров карточка обещала быструю выдачу. */}
            <div
              className={`absolute top-4 left-4 flex items-center gap-3 bg-[#0d1117]/90 border text-sm font-medium px-5 py-2 rounded-full z-10 ${
                cheapestListing?.slow_delivery
                  ? 'border-amber-500/30 text-amber-400'
                  : 'border-green-500/30 text-green-400'
              }`}
            >
              {cheapestListing?.slow_delivery ? (
                <>
                  <Clock className="w-7 h-7" />
                  Доставка до 12 часов
                </>
              ) : (
                <>
                  <Zap className="w-7 h-7" />
                  Время доставки: 1 мин
                </>
              )}
            </div>

            {/* Оверлей: просмотры — сверху-справа */}
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-[#0d1117]/80 border border-[#30363d] text-[#8b949e] text-sm px-4 py-2 rounded-full z-10">
              <Eye className="w-7 h-7" />
              {getFakeViewCount(card.market_hash_name)}
            </div>

            {/* Оверлей: автовозврат — снизу-слева */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute bottom-4 left-4 p-1.5 bg-[#0d1117]/80 border border-green-500/30 rounded-full cursor-default z-10">
                  <ShieldCheck className="w-4 h-4 text-green-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                Безопасный Trade процесс. Если вы не получили товар — средства возвращаются на ваш баланс
              </TooltipContent>
            </Tooltip>

            {card.image ? (
              <img
                src={card.image}
                alt={card.title}
                className="max-w-full max-h-[600px] object-contain drop-shadow-2xl"
              />
            ) : (
              <div className="w-64 h-64 bg-[#0d1117] rounded-lg flex items-center justify-center">
                <span className="text-[#8b949e]">Нет изображения</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            {/* Title & Rarity */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold text-white">{card.title}</h1>
              </div>
              {card.rarity && (
                <Badge 
                  className="text-sm border"
                  style={{ 
                    backgroundColor: card.rarity_color ? `${card.rarity_color}20` : '#88888820',
                    borderColor: card.rarity_color || '#888888',
                    color: card.rarity_color || '#888888'
                  }}
                >
                  {card.rarity}
                </Badge>
              )}
              {card.collection && (
                <p className="text-[#8b949e] mt-2">Коллекция: {card.collection}</p>
              )}
            </div>

            {/* Версия: обычная / StatTrak™ / сувенирная.
                Рисуем только реально существующие варианты и только если их
                больше одного — иначе переключать не между чем. Сувенирная
                бывает втрое дешевле обычной, раньше её тут не было вовсе. */}
            {(() => {
              const rv = product?.related_versions;
              if (!rv) return null;
              const opts = ([
                ['normal',   'Обычная',   rv.normal],
                ['stattrak', 'StatTrak™', rv.stattrak],
                ['souvenir', 'Сувенирная', rv.souvenir],
              ] as const).filter(([, , v]) => v);
              if (opts.length < 2) return null;

              const mhn = card.market_hash_name;
              const currentKey = mhn.startsWith('StatTrak™')
                ? 'stattrak'
                : mhn.startsWith('Souvenir')
                  ? 'souvenir'
                  : 'normal';

              return (
                <div className="bg-[#161b22] rounded-xl p-4 border border-[#30363d]">
                  <p className="text-[#8b949e] text-sm mb-3">Версия</p>
                  <div className={`grid gap-2 ${opts.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {opts.map(([key, label, v]) => {
                      const active = key === currentKey;
                      return (
                        <button
                          key={key}
                          onClick={() => !active && navigate(`/market/${v!.slug}`)}
                          disabled={active}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            active
                              ? 'bg-[#ff6b00]/20 border-[#ff6b00] text-white'
                              : 'bg-[#0d1117] border-[#30363d] text-[#8b949e] hover:border-[#8b949e]'
                          }`}
                        >
                          <p className="font-medium">{label}</p>
                          <p className="text-sm opacity-80">
                            {v!.lowest_price
                              ? `от ${Math.round(v!.lowest_price).toLocaleString('ru-RU')} ₽`
                              : 'Нет в наличии'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Характеристики экземпляра, цену которого показываем ниже.
                Данные — из расширенного прайса; у кейсов и наклеек их нет,
                тогда компонент ничего не рисует. */}
            <ItemAttributes
              floatValue={cheapestListing?.float_value}
              paintSeed={cheapestListing?.paint_seed}
              phase={cheapestListing?.phase}
              category={card.category}
              rarity={card.rarity}
              rarityColor={card.rarity_color}
              wear={cheapestListing?.wear}
              nametag={cheapestListing?.nametag}
              stickers={cheapestListing?.stickers}
              keychains={cheapestListing?.keychains}
            />

            {/* Price Stats & Buy Button */}
            <div className="bg-[#161b22] rounded-xl p-6 border border-[#30363d]">
              <p className="text-[#8b949e] mb-2">Цена от</p>
              <p className="text-4xl font-bold text-[#ff6b00]">
                {aggregate_offer.low_price > 0 
                  ? `${Math.round(aggregate_offer.low_price).toLocaleString('ru-RU')} ₽` 
                  : 'Нет в наличии'}
              </p>
              {aggregate_offer.high_price > aggregate_offer.low_price && (
                <div className="flex items-center gap-4 mt-2 text-sm text-[#8b949e]">
                  <span>До: {Math.round(aggregate_offer.high_price).toLocaleString('ru-RU')} ₽</span>
                  <span>•</span>
                  <span>{aggregate_offer.offer_count} {aggregate_offer.offer_count === 1 ? 'предложение' : aggregate_offer.offer_count < 5 ? 'предложения' : 'предложений'}</span>
                </div>
              )}
              
              {/* Buy Buttons */}
              {cheapestListing && (
                <div className="mt-4 space-y-2">
                  <Button
                    className="w-full bg-[#ff6b00] hover:bg-[#ff8533] text-white font-semibold py-6"
                    onClick={handleBuyClick}
                    disabled={!cheapestListing || buying}
                  >
                    {buying ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Создание платежа...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Купить картой за {Math.round(cheapestListing.price).toLocaleString('ru-RU')} ₽
                      </>
                    )}
                  </Button>

                  {/* Купить за баланс — только если хватает */}
                  {balance !== null && balance >= Math.round(cheapestListing.price) && (
                    <Button
                      variant="outline"
                      className="w-full border-[#30363d] text-white hover:bg-[#161b22] font-semibold py-6"
                      onClick={() => {
                        if (!authApi.isAuthenticated()) { navigate('/login'); return; }
                        handleConfirmBuy('balance');
                      }}
                      disabled={buying}
                    >
                      <Wallet className="w-5 h-5 mr-2 text-[#ff6b00]" />
                      Купить за баланс ({balance.toLocaleString('ru-RU')} ₽)
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Trust indicators — скорость + автовозврат */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-[#8b949e]">
              <div className="flex items-center gap-1.5">
                {cheapestListing?.slow_delivery ? (
                  <>
                    <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span>Ручная выдача — до 12 часов</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 text-[#ff6b00] flex-shrink-0" />
                    <span>Доставка ~1 минута</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span>Автоматический возврат средств</span>
              </div>
            </div>

            {/* Description */}
            {card.description && (
              <p className="text-[#8b949e]">{card.description}</p>
            )}
          </div>
        </div>

        {/* Filters & Listings */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="space-y-6">
            <div className="bg-[#161b22] rounded-xl p-6 border border-[#30363d]">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-[#ff6b00]" />
                <h3 className="text-white font-semibold">Фильтры</h3>
              </div>

              {/* Wear Filter */}
              {available_filters?.wears?.length > 0 && (
                <div className="mb-6">
                  <p className="text-[#8b949e] text-sm mb-3">Качество</p>
                  <div className="space-y-2">
                    {available_filters.wears.map((wear) => (
                      <button
                        key={wear.value}
                        onClick={() => handleWearToggle(wear.value)}
                        className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                          selectedWear === wear.value
                            ? 'bg-[#ff6b00]/20 border border-[#ff6b00]/50'
                            : 'bg-[#0d1117] border border-transparent hover:border-[#30363d]'
                        }`}
                      >
                        <span className={selectedWear === wear.value ? 'text-white' : 'text-[#8b949e]'}>
                          {wearRu(wear.label)}
                        </span>
                        <Badge variant="outline" className="text-xs border-[#30363d] text-[#8b949e]">
                          {wear.count}
                        </Badge>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Separator className="bg-[#30363d] my-4" />

              {/* StatTrak Toggle */}
              {available_filters.has_stattrak && (
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-white">Только StatTrak™</p>
                    <p className="text-xs text-[#8b949e]">{available_filters.stattrak_count} шт.</p>
                  </div>
                  <Switch
                    checked={stattrakOnly}
                    onCheckedChange={handleStattrakToggle}
                  />
                </div>
              )}

              {/* Stickers Toggle */}
              {available_filters.has_stickers && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">С наклейками</p>
                    <p className="text-xs text-[#8b949e]">{available_filters.stickers_count} шт.</p>
                  </div>
                  <Switch
                    checked={stickersOnly}
                    onCheckedChange={handleStickersToggle}
                  />
                </div>
              )}

              {/* Float Range (можно добавить позже) */}
            </div>
          </div>

          {/* Listings */}
          <div className="lg:col-span-3">
            {/* Sort */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-[#8b949e]">
                Найдено: <span className="text-white">{listings?.length || 0}</span> предложений
              </p>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-[#8b949e]" />
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    updateFilters({ sort: e.target.value });
                  }}
                  className="bg-[#161b22] border border-[#30363d] text-white rounded-lg px-3 py-2 text-sm focus:border-[#ff6b00] focus:outline-none"
                >
                  <option value="price_asc">Цена: по возрастанию</option>
                  <option value="price_desc">Цена: по убыванию</option>
                  <option value="float_asc">Float: по возрастанию</option>
                  <option value="float_desc">Float: по убыванию</option>
                </select>
              </div>
            </div>

            {/* Listings Table */}
            <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0d1117]">
                    <tr>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm w-14"></th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Флоат</th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Паттерн</th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Наклейки</th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Брелок</th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Именной ярлык</th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Доставка</th>
                      <th className="text-left text-[#8b949e] font-medium p-4 text-sm">Цена</th>
                      <th className="text-right text-[#8b949e] font-medium p-4 text-sm"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#30363d]">
                    {listings?.map((listing) => (
                      <tr key={listing.id} className="hover:bg-[#0d1117]/50">
                        {/* Миниатюра скина */}
                        <td className="p-2 pl-4">
                          <div className="w-[60px] h-[60px] bg-[#0d1117] rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img
                              src={card.image}
                              alt={card.title}
                              className="w-full h-full object-contain"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          </div>
                        </td>
                        <td className="p-4 min-w-[150px]">
                          <div>
                            <p className="text-white font-mono text-sm">
                              {listing.float_value != null ? listing.float_value.toFixed(8) : '—'}
                            </p>
                            {listing.float_value != null && (
                              <div className="mt-1 mb-1">
                                <FloatBar value={listing.float_value} compact />
                              </div>
                            )}
                            <p className="text-xs text-[#8b949e]">{wearRu(listing.wear)}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="text-white font-mono text-sm">
                            {listing.paint_seed != null ? `#${listing.paint_seed}` : '—'}
                          </p>
                        </td>
                        <td className="p-4">
                          <StickerRow stickers={listing.stickers} compact />
                        </td>
                        <td className="p-4">
                          <StickerRow keychains={listing.keychains} compact />
                        </td>
                        <td className="p-4 max-w-[140px]">
                          {listing.nametag ? (
                            <p className="text-white text-sm italic truncate" title={listing.nametag}>
                              «{listing.nametag}»
                            </p>
                          ) : (
                            <span className="text-[#6e7681] text-sm">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {listing.slow_delivery ? (
                                <Clock className="w-4 h-4 text-amber-400 cursor-default" />
                              ) : (
                                <Zap className="w-4 h-4 text-green-400 cursor-default" />
                              )}
                            </TooltipTrigger>
                            <TooltipContent>
                              {listing.slow_delivery
                                ? 'Доставка до 12 часов'
                                : 'Скорость доставки — 1 минута'}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="p-4">
                          <p className="text-[#ff6b00] font-bold text-lg">
                            {Math.round(listing.price).toLocaleString('ru-RU')} ₽
                          </p>
                        </td>
                        <td className="p-4 text-right">
                          <Link to={`/listing/${listing.id}`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#ff6b00] text-[#ff6b00] hover:bg-[#ff6b00] hover:text-white"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Подробнее
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {listings && listings.length > 0 && listings.every(l => !l.float_value) && (
                <div className="flex items-start gap-2 px-4 py-3 bg-[#0d1117] border-t border-[#30363d]">
                  <Info className="w-4 h-4 text-[#8b949e] flex-shrink-0 mt-0.5" />
                  <p className="text-[#8b949e] text-xs">
                    Данные о float/наклейках недоступны для данного предложения
                  </p>
                </div>
              )}

              {(!listings || listings.length === 0) && (
                <div className="text-center py-12">
                  <p className="text-[#8b949e]">Нет предложений с выбранными фильтрами</p>
                  <Button
                    variant="outline"
                    className="mt-4 border-[#30363d] text-[#8b949e]"
                    onClick={() => {
                      setSelectedWear(null);
                      setStattrakOnly(false);
                      setStickersOnly(false);
                      setSearchParams({});
                    }}
                  >
                    Сбросить фильтры
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trade URL Required Dialog */}
      <TradeUrlRequiredDialog
        open={showTradeUrlDialog}
        onClose={() => setShowTradeUrlDialog(false)}
        onSaved={() => {
          setShowTradeUrlDialog(false);
          handleConfirmBuy();
        }}
      />

      <OrderStatusModal
        transactionId={orderStatusTxId}
        onClose={() => { setOrderStatusTxId(null); setPendingPayment(null); sessionStorage.removeItem('xpanda_pending_tx'); }}
        onPaymentConfirmed={() => {
          paymentPopupRef.current?.close();
          paymentPopupRef.current = null;
          setPendingPayment(null);
        }}
        paymentUrl={pendingPayment?.url}
        paymentFormData={pendingPayment?.formData}
      />
    </div>
  );
}
